import {
  Box,
  Tag as ChakraTag,
  TagProps as ChakraTagProps,
  HStack,
  PlacementWithLogical,
  TagLabel,
  TagLeftIcon,
  TagRightIcon,
} from '@chakra-ui/react';
import { selectedStateStyles } from 'Atoms/SelectedStateStyles';
import { TruncatableText } from 'Atoms/TruncatableText';
import { HelpTooltip } from 'Molecules';
import React from 'react';
import { TagTheme } from './TagTheme';
import { Tooltip } from 'Atoms/Tooltip';

//
// See tag styles in TagTheme.tsx
//

export const TAG_VARIANTS = Object.keys(TagTheme.variants ?? {});
export type TagVariant = (typeof TAG_VARIANTS)[number];

export const TAG_SIZES = Object.keys(TagTheme.sizes ?? {});
export type TagSize = (typeof TAG_SIZES)[number];

export type TagProps = Omit<ChakraTagProps, 'variant'> & {
  variant?: TagVariant;
  leftIcon?: ChakraTagProps['children'];
  rightIcon?: ChakraTagProps['children'];
  title?: ChakraTagProps['children'];
  size?: TagSize;
  rightIconMargin?: string;
  leftIconMargin?: string;
  helpLabel?: string;
  helpTooltipPlacement?: PlacementWithLogical;
  clickable?: boolean;
  isSelected?: boolean;
  truncatable?: boolean;
  maxWidthInPixels?: number;
  disabled?: boolean;
  onRemove?: () => void;
  tagTooltip?: string;
  textWidth?: string;
};

export const Tag = React.forwardRef(
  (
    {
      title,
      size = 'sm',
      variant = 'default',
      rightIconMargin = '0px',
      leftIcon,
      rightIcon,
      maxWidthInPixels,
      backgroundColor,
      isSelected = false,
      truncatable = false,
      helpTooltipPlacement,
      clickable = false,
      disabled = false,
      onRemove,
      tagTooltip,
      textWidth,
      gap,
      ...props
    }: TagProps,
    ref: React.LegacyRef<HTMLSpanElement>
  ) => {
    const customHoverAndActiveStyles = {
      _hover: clickable ? props._hover : {},
      _active: clickable ? props._active : {},
    };

    return (
      <Tooltip label={tagTooltip} visibility={tagTooltip ? 'visible' : 'hidden'}>
        <ChakraTag
          ref={ref}
          size={size}
          variant={variant}
          disabled={disabled}
          backgroundColor={backgroundColor}
          {...props}
          {...customHoverAndActiveStyles}
          sx={isSelected ? selectedStateStyles : {}}
          gap={gap}
          justifyContent="space-between"
        >
          <>
            {leftIcon && (
              <TagLeftIcon
                children={leftIcon}
                boxSize={size === 'xs' ? '12px' : '16px'}
                marginRight={gap ? '0px' : size === 'xs' ? '2px' : '4px'}
                color={props.color}
              />
            )}
            {title ? (
              <TagLabel
                paddingLeft={leftIcon && !gap ? '2px' : '0px'}
                maxWidth={maxWidthInPixels}
                lineHeight="normal"
                textAlign="center"
                width={textWidth}
              >
                {truncatable ? (
                  <TruncatableText
                    variant={size === 'xs' ? 'detailStrong' : 'bodyStrong'}
                    color="currentColor"
                    text={title}
                    maxTextWidth={maxWidthInPixels}
                  />
                ) : (
                  title
                )}
              </TagLabel>
            ) : (
              props.children
            )}
            {rightIcon && (
              <TagRightIcon
                children={rightIcon}
                boxSize={size === 'xs' ? '12px' : '16px'}
                marginLeft={gap ? '0px' : size === 'xs' ? '2px' : '4px'}
                color={props.color}
                onClick={onRemove}
                cursor={!!onRemove ? 'pointer' : 'default'}
                _hover={!!onRemove ? { bg: 'bg.hover' } : {}}
                borderRadius="4px"
              />
            )}
            {props.helpLabel && (
              <TagLabel marginLeft={rightIconMargin}>
                <HelpTooltip
                  label={props.helpLabel}
                  iconColor="currentColor"
                  placement={helpTooltipPlacement}
                />
              </TagLabel>
            )}
          </>
        </ChakraTag>
      </Tooltip>
    );
  }
);

export const AdditionalTypeTag = ({
  label,
  maxWidthInPixels,
  description,
  onHelpClick,
}: {
  label: string;
  maxWidthInPixels?: number;
  description?: string;
  onHelpClick?: () => void;
}) => {
  return (
    <Tag maxWidth={maxWidthInPixels}>
      <HStack>
        <TruncatableText text={label} maxTextWidth={maxWidthInPixels}></TruncatableText>
        <Box onClick={onHelpClick}>
          <HelpTooltip label={description} />
        </Box>
      </HStack>
    </Tag>
  );
};
